import { apiInstance, apiSurveyInstance } from './utils';
import { UserAuthType } from './types';
import { OrganizationType } from 'store/modules/organizations/organizations';
import { SearchParamsType } from 'api/types';
import briefings from 'api/resources/products/briefings/briefings';
import products from 'api/resources/products/products';
import workbenches from 'api/resources/products/workbenches/workbenches';
import projects from 'api/resources/org/projects/projects';
import reports from 'api/resources/org/reports/reports';
import assets from 'api/resources/products/assets';
import auth from 'api/resources/auth';
import bexio from 'api/resources/bexio';
import payments from 'api/resources/payments';
import templates from 'api/resources/org/templates/email';
import assistant from 'api/resources/assistant';
import { organizationUsers } from 'api/resources/org/admin/users';
import { organizationGroups } from './resources/org/admin/groups';
import { organizationProjects } from './resources/org/admin/projects';
import { organizationProduct } from './resources/org/admin/products';

export default {
  signInUser: ({ email, password }: UserAuthType) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/login/',
      data: {
        email: email.toLowerCase(),
        password,
      },
    }),
  signUpUser: ({
    email,
    password,
    name,
    first_name,
    last_name,
    terms_agreement,
    privacy_policy_accepted,
  }: UserAuthType & {
    name: string;
    first_name: string;
    last_name: string;
    terms_agreement: boolean;
    privacy_policy_accepted: boolean;
  }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/signup/',
      data: {
        email: email.toLowerCase(),
        password,
        name: name,
        first_name,
        last_name,
        terms_agreement,
        privacy_policy_accepted,
      },
    }),
  verifyAccount: ({ token }: { token: string }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/verify/',
      data: { token },
    }),
  userProjects: ({
    params,
    organizationName,
  }: {
    organizationName: string;
    params?: {
      limit: string;
      offset: string;
      search: string;
    };
  }) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organizationName}/projects/`,
      params: {
        ...params,
        ordering: '-created_at',
      },
    }),
  refreshToken: ({ token }: { token: string }) => {
    return apiInstance.request({
      method: 'POST',
      url: '/auth/refresh/',
      data: {
        token,
      },
    });
  },
  getProjectSurveys: ({ orgName, uuid }: { orgName: string; uuid: string }) => {
    return apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/surveys/`,
      params: {
        project_uuid: uuid,
      },
    });
  },
  getSurveysCSV: ({ orgName, uuid }: { orgName: string; uuid: string }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/surveys/${uuid}/csv/`,
    }),
  generateSurveyKeys: ({
    orgName,
    uuid,
    country,
    count,
  }: {
    orgName: string;
    uuid: string;
    country: string;
    count: number;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/surveys/${uuid}/csv/`,
      params: {
        country,
        count,
      },
    }),
  generateSurveyUrls: ({
    orgName,
    uuid,
    country,
    count,
    parameters,
  }: {
    orgName: string;
    uuid: string;
    country?: string;
    count: number;
    parameters?: { [k: string]: string };
  }) =>
    apiSurveyInstance.request({
      method: 'POST',
      url: `/org/${orgName}/surveys/${uuid}/generate-urls/`,
      data: {
        country: country === 'XX' ? undefined : country,
        number_of_keys: count,
        parameters,
      },
    }),
  getAssistantGenders: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/genders/',
    }),
  getAssistantCategories: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/categories/',
    }),
  getAssistantCategory: ({ categoryId }: { categoryId: number }) =>
    apiInstance.request({
      method: 'GET',
      url: `/assistant/categories/${categoryId}/`,
    }),
  getAssistantSubcategories: ({ categoryId }: { categoryId: number }) =>
    apiInstance.request({
      method: 'GET',
      url: `/assistant/categories/${categoryId}/subcategories/`,
    }),
  getAssistantAudienceSegments: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/audience/segments/',
    }),
  getAssistantCountries: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/countries/',
    }),
  getAssistantTypes: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/types/',
    }),
  getAssistantResearchGoals: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/research/goals/',
    }),
  getAssistantAudienceSize: () =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/audience/size-range/',
    }),
  updateUserProfile: (data: { [key: string]: any }) =>
    apiInstance.request({
      method: 'PATCH',
      url: '/auth/profile/',
      data,
    }),
  updateUserPassword: ({
    current_password,
    new_password,
    new_password_confirm,
  }: {
    current_password: string;
    new_password: string;
    new_password_confirm: string;
  }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/profile/set/password/',
      data: {
        current_password,
        new_password,
        new_password_confirm,
      },
    }),
  updateUserEmail: ({ new_email }: { new_email: string }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/profile/set/email/',
      data: {
        new_email,
      },
    }),
  forgotPasswordSendEmail: ({ email }: { email: string }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/password/reset/send/',
      data: {
        email,
      },
    }),
  forgotPasswordNewPassword: ({
    new_password,
    new_password_confirm,
    reset_key,
  }: {
    new_password: string;
    new_password_confirm: string;
    reset_key: string;
  }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/password/reset/',
      data: {
        new_password,
        new_password_confirm,
        reset_key,
      },
    }),
  archiveProject: (organizationName: string, projectId: number) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/org/${organizationName}/projects/${projectId}/archive/`,
    }),
  unarchiveProject: (organizationName: string, projectId: number) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/org/${organizationName}/projects/${projectId}/restore/`,
    }),
  confirmEmail: ({ confirmation_key }: { confirmation_key: string }) =>
    apiInstance.request({
      method: 'POST',
      url: '/auth/email/confirm/',
      data: {
        confirmation_key,
      },
    }),
  createProjectWithoutAuth: (
    data: object,
    headers?: { [key: string]: string }
  ) =>
    apiInstance.request({
      method: 'POST',
      url: '/projects/create/',
      data,
      headers,
    }),
  getOrganizations: (params?: {
    limit: string;
    offset: string;
    search: string;
  }) =>
    apiInstance.request({
      method: 'GET',
      url: '/organizations/',
      params,
    }),
  getOrganization: ({ id }: { id: string | number }) =>
    apiInstance.request({
      method: 'GET',
      url: `/organizations/${id}/`,
    }),
  updateOrganization: ({
    id,
    data,
  }: {
    id: string | number;
    data: OrganizationType;
  }) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/organizations/${id}/`,
      data,
    }),
  updateOrganizationLogo: ({ logo, id }: { logo: FormData; id: number }) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/organizations/${id}/`,
      data: logo,
    }),
  getOrganizationProjects: ({
    params,
    organization_name,
  }: {
    organization_name: string;
    params?: { limit: string; offset: string };
  }) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_name}/projects/`,
      params: {
        ...params,
        ordering: '-created_at',
      },
    }),
  acceptProjects: ({
    id,
    organization_name,
  }: {
    organization_name: string;
    id: number;
  }) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/org/${organization_name}/projects/${id}/accept/`,
    }),
  getTemplates: (params?: { limit: string; offset: string }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/public/templates/surveys/`, // TODO REMOVE PUBLIC WHEN BE WILL FIX API
      params,
    }),
  getTemplate: (id: number) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/public/templates/surveys/${id}/`, // TODO REMOVE PUBLIC WHEN BE WILL FIX API
    }),
  createTemplate: (params: { name: string; json: any }) =>
    apiSurveyInstance.request({
      method: 'POST',
      url: `/org/public/templates/surveys/`, // TODO REMOVE PUBLIC WHEN BE WILL FIX API
      data: params,
    }),
  editTemplate: (id: number, params: { name: string; json: any }) =>
    apiSurveyInstance.request({
      method: 'PUT',
      url: `/org/public/templates/surveys/${id}/`, // TODO REMOVE PUBLIC WHEN BE WILL FIX API
      data: params,
    }),
  getOrganizationPayments: (
    organization_codename: string,
    params?: SearchParamsType
  ) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_codename}/payments/tokens/`,
      params: {
        ordering: '-created_at',
        ...params,
      },
    }),
  finishDataCollection: (organization_codename: string, id: string) =>
    apiInstance.request({
      method: 'POST',
      url: `/org/${organization_codename}/projects/${id}/finish/`,
    }),
  getOrganizationCredits: (
    organization_codename: string
    // params?: SearchParamsType
  ) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_codename}/payments/token-event/?ordering=-created_at/`,
      // params: {
      //   ordering: '-created_at',
      //   ...params,
      // },
    }),
  createPaymentAddress: ({
    organization_codename,
    data,
  }: {
    organization_codename: string;
    data: any;
  }) =>
    apiInstance.request({
      method: 'POST',
      url: `/org/${organization_codename}/organizations/info/`,
      data: {
        ...data,
      },
    }),
  deletePaymentAddress: ({
    organization_codename,
    id,
  }: {
    organization_codename: string;
    id: string | number;
  }) =>
    apiInstance.request({
      method: 'DELETE',
      url: `/org/${organization_codename}/organizations/info/${id}/`,
    }),
  createPayment: ({
    organization_codename,
    data,
  }: {
    organization_codename: string;
    data: any;
  }) =>
    apiInstance.request({
      method: 'POST',
      url: `/org/${organization_codename}/payments/tokens/`,
      data: {
        ...data,
      },
    }),
  getOrganizationPaymentLimits: (organization_codename: string) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_codename}/payments/limits/`,
    }),
  getOrganizationProject: ({
    id,
    organization_name,
  }: {
    id: number;
    organization_name: string;
  }) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_name}/projects/${id}/`,
    }),
  getOrganizationInfo: (organization_codename: string) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_codename}/organizations/info/`,
      params: {
        ordering: '-id',
      },
    }),
  getTranslations: ({ language_code }: { language_code: string }) =>
    apiInstance.request({
      method: 'GET',
      url: `/localization/${language_code}/dashboard.json/`,
    }),
  removeTemplate: (id: number) =>
    apiSurveyInstance.request({
      method: 'DELETE',
      url: `/org/public/templates/surveys/${id}/`, // TODO REMOVE PUBLIC WHEN BE WILL FIX API
    }),
  getOrganizationUsers: ({
    params,
    organization_name,
  }: {
    organization_name: string;
    params?: { limit: string; offset: string; search: string };
  }) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_name}/users/`,
      params,
    }),
  inviteOrganizationUser: ({
    organization_name,
    emails,
  }: {
    organization_name: string | null;
    emails: string[];
  }) =>
    apiInstance.request({
      method: 'POST',
      url: `/org/${organization_name}/users/invite/`,
      data: {
        emails,
      },
    }),
  getOrganizationInvitations: ({
    params,
    organization_name,
  }: {
    organization_name: string;
    params?: { limit: string; offset: string };
  }) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_name}/users/invite/`,
      params,
    }),
  acceptOrganizationInvitation: ({
    organization_name,
    key,
    new_password,
    new_password_confirm,
  }: {
    organization_name: string;
    key: string;
    new_password: string;
    new_password_confirm: string;
  }) =>
    apiInstance.request({
      method: 'PUT',
      url: `/org/${organization_name}/users/invite/${key}/`,
      data: {
        new_password_confirm,
        new_password,
      },
    }),
  removeOrganizationUser: ({
    uuid,
    organization_name,
  }: {
    organization_name: string;
    uuid: string;
  }) =>
    apiInstance.request({
      method: 'DELETE',
      url: `/org/${organization_name}/users/${uuid}/`,
    }),
  removeInvitationOrganizationUser: ({
    key,
    organization_name,
  }: {
    organization_name: string;
    key: string;
  }) =>
    apiInstance.request({
      method: 'DELETE',
      url: `/org/${organization_name}/users/invite/${key}/`,
    }),
  updateOrganizationUser: ({
    uuid,
    organization_name,
    user,
  }: {
    organization_name: string;
    uuid: string;
    user: { [key: string]: any };
  }) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/org/${organization_name}/users/${uuid}/`,
      data: user,
    }),
  getOrganizationUser: ({
    uuid,
    organization_name,
  }: {
    organization_name: string;
    uuid: string;
  }) =>
    apiInstance.request({
      method: 'GET',
      url: `/org/${organization_name}/users/${uuid}/`,
    }),
  demoteOrganizationUser: ({
    uuid,
    organization_name,
  }: {
    organization_name: string;
    uuid: string;
  }) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/org/${organization_name}/users/${uuid}/demote/`,
    }),
  promoteOrganizationUser: ({
    uuid,
    organization_name,
  }: {
    organization_name: string;
    uuid: string;
  }) =>
    apiInstance.request({
      method: 'PATCH',
      url: `/org/${organization_name}/users/${uuid}/promote/`,
    }),
  uploadRespondantsList: ({
    uuid,
    codename,
    file,
  }: {
    codename: string;
    uuid: string;
    file: any;
  }) =>
    apiSurveyInstance.request({
      method: 'PUT',
      url: `/org/${codename}/surveys/respondents/${uuid}/upload/`,
      data: file,
      headers: {
        'Content-Type': 'text/csv',
      },
    }),
  getUsers: ({
    params,
  }: {
    params?: { limit: string; offset: string; search: string };
  }) =>
    apiInstance.request({
      method: 'GET',
      url: '/users/',
      params,
    }),
  getUser: ({ uuid }: { uuid: string }) =>
    apiInstance.request({
      method: 'GET',
      url: `/users/${uuid}/`,
    }),
  getSurveysStatistics: ({
    orgName,
    uuid,
  }: {
    orgName: string;
    uuid: string;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/surveys/statistics/${uuid}/`,
    }),
  getProjectTableResults: (orgName: string, projectUUID: string) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/reports/tables/`,
      params: { project_uuid: projectUUID },
    }),
  getProjectTableRuns: (orgName: string, tableId: string) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/reports/scripts/runs/`,
      params: { report_table: tableId },
    }),
  getProjectRunFiles: (orgName: string, runId: string) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: `/org/${orgName}/reports/scripts/runs/${runId}/files/`,
    }),
  getFileCsv: (orgName: string, runId: number, fileName: string) =>
    apiSurveyInstance.request({
      method: 'POST',
      url: `/org/${orgName}/reports/scripts/runs/${runId}/download/`,
      data: { name: fileName },
      responseType: 'blob',
    }),
  getAssistantBusinessSectors: ({ search }: { search: string }) =>
    apiInstance.request({
      method: 'GET',
      url: '/assistant/sectors/',
      params: {
        search,
        limit: 100,
      },
    }),
  getAssistantBusinessSector: ({ id }: { id: number }) =>
    apiInstance.request({
      method: 'GET',
      url: `/assistant/sectors/${id}/`,
    }),
  getAssistantBusinessSubsectors: ({ id }: { id: number }) =>
    apiInstance.request({
      method: 'GET',
      url: `/assistant/sectors/${id}/subsectors/`,
    }),
  getRespondentsList: ({
    uuid,
    parsedSearch,
  }: {
    uuid: string;
    parsedSearch?: any;
  }) =>
    apiSurveyInstance.request({
      method: 'GET',
      url: '/keys/keys_to_send/',
      params: {
        survey_uuid: uuid,
        limit: parsedSearch.limit,
        offset: parsedSearch.offset,
        ordering: parsedSearch.ordering,
      },
    }),
  deleteEmail: ({ uuid }: { uuid: string }) =>
    apiSurveyInstance.request({
      method: 'DELETE',
      url: `/keys/${uuid}/`,
    }),
  sendSurveysToRespondents: ({
    orgName,
    uuid,
    data,
  }: {
    orgName: string;
    uuid: string;
    data: any;
  }) => {
    return apiSurveyInstance.request({
      method: 'POST',
      url: `/org/${orgName}/surveys/respondents/${uuid}/send/`,
      data: data,
    });
  },
  ...briefings,
  ...products,
  ...workbenches,
  ...projects,
  ...reports,
  ...assets,
  ...auth,
  organization: {
    users: organizationUsers,
    groups: organizationGroups,
    projects: organizationProjects,
    products: organizationProduct,
  },
  bexio,
  payments,
  templates,
  assistant,
};
